import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Analytics = ({ userData }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchStats();
  }, [selectedDate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/analytics/daily-stats?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      setError('Failed to load analytics data');
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return num ? num.toLocaleString() : '0';
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Daily Analytics</h1>
      
      <div style={styles.dateSelector}>
        <label style={styles.label}>Select Date:</label>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          style={styles.dateInput}
        />
        <button onClick={fetchStats} style={styles.refreshButton}>
          Refresh
        </button>
      </div>

      {stats && (
        <>
          {/* Summary Cards */}
          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <h3 style={styles.summaryTitle}>Unique Logins</h3>
              <div style={styles.summaryNumber}>{formatNumber(stats.summary.unique_logins)}</div>
            </div>
            <div style={styles.summaryCard}>
              <h3 style={styles.summaryTitle}>Login Events</h3>
              <div style={styles.summaryNumber}>{formatNumber(stats.summary.login_events)}</div>
            </div>
            <div style={styles.summaryCard}>
              <h3 style={styles.summaryTitle}>App Sessions</h3>
              <div style={styles.summaryNumber}>{formatNumber(stats.summary.app_session_events)}</div>
            </div>
            <div style={styles.summaryCard}>
              <h3 style={styles.summaryTitle}>Logout Events</h3>
              <div style={styles.summaryNumber}>{formatNumber(stats.summary.logout_events)}</div>
            </div>
          </div>

          {/* Role Breakdown */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Activity by Role</h2>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Role</th>
                    <th style={styles.th}>Unique Users</th>
                    <th style={styles.th}>Total Events</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.by_role.map((role, index) => (
                    <tr key={index}>
                      <td style={styles.td}>{role.role}</td>
                      <td style={styles.td}>{formatNumber(role.unique_users)}</td>
                      <td style={styles.td}>{formatNumber(role.total_events)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* School Breakdown */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Activity by School</h2>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>School</th>
                    <th style={styles.th}>Unique Users</th>
                    <th style={styles.th}>Total Events</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.by_school.map((school, index) => (
                    <tr key={index}>
                      <td style={styles.td}>{school.school_name}</td>
                      <td style={styles.td}>{formatNumber(school.unique_users)}</td>
                      <td style={styles.td}>{formatNumber(school.total_events)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  title: {
    fontSize: '2.5rem',
    marginBottom: '2rem',
    color: '#1D4E89',
    textAlign: 'center'
  },
  dateSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '2rem',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px'
  },
  label: {
    fontWeight: '500',
    color: '#333'
  },
  dateInput: {
    padding: '0.5rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem'
  },
  refreshButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem'
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem'
  },
  summaryCard: {
    backgroundColor: '#f8f9fa',
    padding: '1.5rem',
    borderRadius: '8px',
    textAlign: 'center',
    border: '1px solid #e9ecef'
  },
  summaryTitle: {
    fontSize: '1rem',
    color: '#666',
    marginBottom: '0.5rem',
    fontWeight: '500'
  },
  summaryNumber: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#1D4E89'
  },
  section: {
    marginBottom: '2rem'
  },
  sectionTitle: {
    fontSize: '1.5rem',
    color: '#333',
    marginBottom: '1rem',
    borderBottom: '2px solid #1D4E89',
    paddingBottom: '0.5rem'
  },
  tableContainer: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  th: {
    backgroundColor: '#1D4E89',
    color: 'white',
    padding: '1rem',
    textAlign: 'left',
    fontWeight: '600'
  },
  td: {
    padding: '1rem',
    borderBottom: '1px solid #e9ecef',
    color: '#333'
  },
  loading: {
    textAlign: 'center',
    fontSize: '1.2rem',
    color: '#666',
    padding: '2rem'
  },
  error: {
    textAlign: 'center',
    fontSize: '1.2rem',
    color: '#dc3545',
    padding: '2rem',
    backgroundColor: '#f8d7da',
    borderRadius: '8px',
    border: '1px solid #f5c6cb'
  }
};

export default Analytics;
